import { computed, unref } from '@vue/composition-api';
import { getMonthsListWithYearsBetweenDates } from '@/util/list-months';
import { useHalfToDate } from '@/util/half-to-date';
import { formatCurrency } from '@/util/currency';
import { getDisplayPriceForAvailability } from '@/views/Brand/Applications/Components/Offers/_utils/monthly-sums';
const floatingTextFiltersParams = {
    filter: true,
    floatingFilter: true,
    floatingFilterComponent: 'FloatingTextFilter',
    suppressFloatingFilterButton: true,
    suppressHeaderMenuButton: true,
};
const romanMonths = [
    'I',
    'II',
    'III',
    'IV',
    'V',
    'VI',
    'VII',
    'VIII',
    'IX',
    'X',
    'XI',
    'XII',
];
const formatAreaInSquareMeters = (width, height) => {
    if (!width || !height)
        return '-';
    // Width/height are stored in millimeters in API payloads.
    const areaInSquareMeters = (width * height) / 1000000;
    return Number(areaInSquareMeters.toFixed(2)).toString();
};
export const useCommonColumns = (ctx, { offer, contract, useOfferObjectDetails, shownYear = new Date().getFullYear(), }) => {
    const monthsInOffer = computed(() => {
        const year = shownYear != null ? unref(shownYear) : new Date().getFullYear();
        const startDate = typeof offer.startDate === 'string'
            ? useHalfToDate(offer.startDate, false)
            : null;
        const endDate = typeof offer.endDate === 'string'
            ? useHalfToDate(offer.endDate, true)
            : null;
        if (!startDate || !endDate)
            return [];
        return getMonthsListWithYearsBetweenDates(startDate, endDate, year);
    });
    const getMonthColumn = (monthWithYear) => ({
        field: 'availability',
        colId: monthWithYear.key,
        sortable: false,
        width: 140,
        headerName: romanMonths[monthWithYear.month - 1],
        cellRenderer: 'AvailabilityCell',
        cellRendererParams: (params) => ({
            availability: params?.data?.availability?.find((item) => item.month === monthWithYear.month &&
                item.year === monthWithYear.year),
            contract,
        }),
    });
    const getYearSummaryColumn = (year) => ({
        field: 'availability',
        colId: `year-summary-${year}`,
        sortable: false,
        width: 150,
        headerName: String(year),
        valueGetter: (params) => {
            const rowData = params?.data;
            if (!rowData)
                return '-';
            const totalForYear = monthsInOffer.value
                .filter((monthWithYear) => monthWithYear.year === year)
                .reduce((sum, monthWithYear) => {
                const availability = rowData.availability?.find((item) => item.month === monthWithYear.month &&
                    item.year === monthWithYear.year);
                const price = getDisplayPriceForAvailability(availability, !!contract);
                return sum + (price ?? 0);
            }, 0);
            return formatCurrency(totalForYear);
        },
    });
    const monthsAndYearsColumns = computed(() => {
        const columns = [];
        monthsInOffer.value.forEach((monthWithYear, index) => {
            columns.push(getMonthColumn(monthWithYear));
            const nextMonth = monthsInOffer.value[index + 1];
            const isEndOfYearSegment = !nextMonth || nextMonth.year !== monthWithYear.year;
            if (isEndOfYearSegment) {
                columns.push(getYearSummaryColumn(monthWithYear.year));
            }
        });
        return columns;
    });
    const commonColumns = computed(() => {
        return [
            {
                field: 'internalNumber',
                width: 90,
                headerName: ctx.root.$t('offer-costs.number').toString(),
                pinned: 'left',
                suppressMovable: true,
                cellRenderer: 'ObjectNumberCell',
                cellRendererParams: (params) => {
                    if (params?.node?.rowPinned)
                        return {};
                    return {
                        offerId: useOfferObjectDetails ? offer.id : null,
                        id: params?.data?.id,
                        internalNumber: params?.data?.internalNumber,
                        objectTypeId: params?.data?.objectType?.id,
                        status: params?.data?.status,
                    };
                },
                floatingFilterComponentParams: {
                    compare: 'startsWith',
                },
                ...floatingTextFiltersParams,
            },
            {
                colId: 'area',
                headerName: ctx.root.$t('offer-costs.area').toString(),
                width: 130,
                valueGetter: (params) => {
                    if (params?.node?.rowPinned)
                        return '';
                    return formatAreaInSquareMeters(params?.data?.width, params?.data?.height);
                },
                floatingFilterComponentParams: {
                    compare: 'startsWith',
                },
                ...floatingTextFiltersParams,
            },
            {
                field: 'city',
                minWidth: 160,
                flex: 2,
                headerName: ctx.root.$t('offer-costs.city').toString(),
                ...floatingTextFiltersParams,
            },
            {
                field: 'address',
                minWidth: 160,
                flex: 2,
                headerName: ctx.root.$t('offer-costs.address').toString(),
                ...floatingTextFiltersParams,
            },
            {
                field: 'place',
                headerName: ctx.root.$t('offer-costs.place').toString(),
                minWidth: 160,
                flex: 1,
                valueGetter: (params) => params?.data?.place?.name,
                ...floatingTextFiltersParams,
            },
            ...monthsAndYearsColumns.value,
        ];
    });
    return { commonColumns };
};
